var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from '@emotion/styled';
import { ExpandMore } from '@mui/icons-material';
import { FormControl, FormHelperText, InputLabel, MenuItem as MuiMenuItem, Select as MuiSelect, } from '@mui/material';
import Text from '../Text';
import React, { useState } from 'react';
import colors from '../../themes/colors';
import { SelectLabel, SelectLabelWithIcon } from './SelectInput.Labels';
var Expand = styled(ExpandMore)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  color: ", " !important;\n"], ["\n  color: ", " !important;\n"])), colors.neutral100);
var SelectInput = React.forwardRef(function (props, ref) {
    var _a, _b, _c;
    var timeoutRef = React.useRef();
    var placeholder = props.placeholder, label = props.label, forceOpen = props.forceOpen, noPadding = props.noPadding, onChange = props.onChange, onBlur = props.onBlur, onSelectClick = props.onSelectClick, renderValue = props.renderValue, value = props.value, options = props.options, children = props.children, _d = props.showExpandIcon, showExpandIcon = _d === void 0 ? true : _d, _e = props.disabled, disabled = _e === void 0 ? false : _e, _f = props.fullWidth, fullWidth = _f === void 0 ? false : _f, _g = props.error, error = _g === void 0 ? false : _g, _h = props.size, size = _h === void 0 ? 'small' : _h, helperText = props.helperText, _j = props.className, className = _j === void 0 ? '' : _j, // specific for ref/styled components
    _k = props.hasDropdownIcons, // specific for ref/styled components
    hasDropdownIcons = _k === void 0 ? false : _k;
    var _l = useState(false), isOpen = _l[0], setIsOpen = _l[1];
    var _m = useState(value), stateValue = _m[0], setStateValue = _m[1];
    var shouldUpdateInternally = forceOpen === undefined;
    var forceOnBlur = function () {
        clearTimeout(timeoutRef.current);
        // trigger blur manually on select
        // https://github.com/mui/material-ui/issues/26076
        timeoutRef.current = window.setTimeout(function () {
            var el = document.activeElement;
            if (el instanceof HTMLElement)
                el.blur();
        }, 0);
    };
    var onCloseSelect = function (event) {
        forceOnBlur();
        event.preventDefault();
        event.stopPropagation();
        onSelectClick === null || onSelectClick === void 0 ? void 0 : onSelectClick(false);
        if (shouldUpdateInternally)
            setIsOpen(false);
    };
    var onOpenSelect = function () {
        onSelectClick === null || onSelectClick === void 0 ? void 0 : onSelectClick(true);
        if (shouldUpdateInternally)
            setIsOpen(true);
    };
    var onSelectValueChange = function (event) {
        event.preventDefault();
        event.stopPropagation();
        var newValue = event.target.value;
        setStateValue(newValue);
        onChange === null || onChange === void 0 ? void 0 : onChange(newValue);
        if (shouldUpdateInternally)
            onCloseSelect(event);
    };
    var defaultValue = placeholder ? 'none' : (_a = options === null || options === void 0 ? void 0 : options[0]) === null || _a === void 0 ? void 0 : _a.value;
    var selectedValue = "".concat(stateValue || defaultValue);
    var selectedLabel = (_c = (_b = options === null || options === void 0 ? void 0 : options.find(function (option) { return option.value === selectedValue; })) === null || _b === void 0 ? void 0 : _b.label) !== null && _c !== void 0 ? _c : placeholder;
    var open = forceOpen || isOpen;
    var labelSize = size === 'medium' ? 'normal' : size;
    var showPlaceholder = Boolean(placeholder && !children && selectedValue === 'none');
    var hiddenIcon = function () { return null; };
    var hiddenIconPaddingOverride = showExpandIcon ? undefined : { sx: { padding: '8px 12px 8px 8px !important' } };
    var defaultRenderValue = function () { return _jsx(Text, __assign({ variant: "body2" }, { children: selectedLabel })); };
    var selectedRenderValue = renderValue !== null && renderValue !== void 0 ? renderValue : defaultRenderValue;
    return (_jsxs(FormControl, __assign({ fullWidth: fullWidth }, { children: [label && _jsx(InputLabel, __assign({ size: labelSize }, { children: label })), _jsxs(MuiSelect, __assign({ disabled: disabled, ref: ref, size: size, error: error, className: className, 
                // This is how we have to hide the icon and handle the padding according to this GitHub issue: https://github.com/mui/material-ui/issues/26473
                IconComponent: showExpandIcon ? Expand : hiddenIcon, inputProps: hiddenIconPaddingOverride, MenuProps: {
                    MenuListProps: __assign(__assign({}, (size === 'small' ? { sx: { padding: '4px 0' } } : { sx: { padding: '8px 0' } })), (noPadding && { sx: { p: 0 } })),
                    PaperProps: {
                        style: {
                            marginTop: 8,
                            border: "2px solid ".concat(colors.neutral20),
                            boxShadow: "0px 2px 4px 2px rgba(82, 99, 99, 0.12)"
                        }
                    }
                }, onBlur: onBlur, onChange: onSelectValueChange, onClick: disabled ? undefined : onOpenSelect, onClose: onCloseSelect, open: open, value: selectedValue, renderValue: selectedRenderValue }, { children: [showPlaceholder && (_jsx(MuiMenuItem, __assign({ className: "select-menu-item-".concat(size), sx: { '&.Mui-selected': { background: colors.white } }, disableRipple: true, value: "none", disabled: true }, { children: _jsx(Text, __assign({ variant: "body2", color: "neutral60" }, { children: placeholder })) }))), children !== null && children !== void 0 ? children : options === null || options === void 0 ? void 0 : options.map(function (option) {
                        var _a;
                        return (_jsx(MuiMenuItem, __assign({ className: "select-menu-item-".concat(size), disableRipple: true, value: option.value, disabled: (_a = option.disabled) !== null && _a !== void 0 ? _a : false }, { children: hasDropdownIcons ? (_jsx(SelectLabelWithIcon, { option: option, selectedValue: selectedValue })) : (_jsx(SelectLabel, { option: option })) }), option.value));
                    })] })), helperText && _jsx(FormHelperText, __assign({ error: error }, { children: helperText }))] })));
});
export default SelectInput;
var templateObject_1;
